import { client } from "@/hono";
import { EditNumericAgreementModal } from "@/modals/partners/edit-numeric-agreement-modal";
import { Button } from "@msuite/ui/button";
import { DataGrid } from "@msuite/ui/data-grid";
import { Divider } from "@msuite/ui/divider";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { moment } from "@msuite/utils/date";
import {
	formatNumericPartnerAgreementValue,
	getPartnerAgreementTypeTranslationKey,
} from "@msuite/utils/partnerships";
import { guardString } from "@msuite/utils/strings";
import type { Tables } from "@msuite/utils/types";
import React from "react";
import UserCard from "../user-card";

/** Props Interface */
interface PartnerAgreementCardProps {
	agreement: Tables<"organizationPartnershipAgreements">;
	followUpAgreement?: Tables<"organizationPartnershipAgreements">;
	agreements: Array<Tables<"organizationPartnershipAgreements">>;
	isActive?: boolean;
	onCompleteEdit: () => Promise<void>;
	orgId: string;
	partnershipId: string;
}

export function PartnerAgreementCard({
	agreement,
	followUpAgreement,
	agreements,
	onCompleteEdit,
	orgId,
	partnershipId,
}: PartnerAgreementCardProps) {
	/** Hooks */
	const { t } = useTranslation();
	const editAgreementModal = useDisclosure();
	const asyncModal = useAsyncModal();
	const validUntil = followUpAgreement?.startDate
		? moment(followUpAgreement.startDate).subtract(1, "day").dateFormat()
		: null;

	/** Functions */
	async function handleOnDeleteAgreement() {
		try {
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			await client.organizations[":orgId"].partnerships[":partnershipId"].agreements[
				":agreementId"
			].$delete({
				param: {
					orgId,
					partnershipId,
					agreementId: agreement.id,
				},
			});
			await onCompleteEdit();
		} catch (error) {
			console.error(error);
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<div className="card v-stack">
				<div className="justify-between h-stack">
					<UserCard
						userId={agreement.createdBy}
						variant="plain"
					/>
					<div className="h-stack">
						<p className="label">
							{moment(agreement.createdAt).longFormat()}{" "}
							{agreement.updatedBy ? `(${t("notes.edited")})` : ""}
						</p>
						<Menu
							items={createStandardMenuItems({
								onEdit: editAgreementModal.onOpen,
								onDelete: handleOnDeleteAgreement,
							})}
						>
							<Button
								icon={<Icon name="EllipsisVertical" />}
								variant="ghost"
							/>
						</Menu>
					</div>
				</div>
				<Divider variant="card" />
				<DataGrid>
					<DataGrid.Row>
						<DataGrid.Label>{t("partners.valid_from")}</DataGrid.Label>
						<DataGrid.Value>{moment(agreement.startDate).dateFormat()}</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("partners.valid_until")}</DataGrid.Label>
						<DataGrid.Value>{guardString(validUntil)}</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>
							{t(getPartnerAgreementTypeTranslationKey(agreement.type))}
						</DataGrid.Label>
						<DataGrid.Value>{formatNumericPartnerAgreementValue(agreement, t)}</DataGrid.Value>
					</DataGrid.Row>
				</DataGrid>
			</div>
			{editAgreementModal.isOpen && (
				<EditNumericAgreementModal
					isOpen={editAgreementModal.isOpen}
					onClose={editAgreementModal.onClose}
					partnershipId={partnershipId}
					orgId={orgId}
					type={agreement.type}
					onCompleteEdit={onCompleteEdit}
					history={agreements}
					agreementId={agreement.id}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("confirmation.confirm_deletion")}
				/>
			)}
		</React.Fragment>
	);
}
